import React, { useLayoutEffect, useRef, useState } from 'react';
import Tooltip from '../utils/Tooltip';
import { tagMeta } from '../../core/criteriaTags';
import './CriteriaTagStrip.css';

const TXT_EN = { fr: 'fr', en: 'en', de: 'de', es: 'es', pt: 'pt' };

const CriteriaTagStrip = ({
    tags = [],
    lang = 'en',
    className = '',
    maxIcons = 6,
    fadeOverflow = false,
    showTooltip = true,
}) => {
    const L = TXT_EN[lang] || TXT_EN.en;
    const stripRef = useRef(null);
    const [overflowing, setOverflowing] = useState(false);
    const metas = (Array.isArray(tags) ? tags : [])
        .map((id) => tagMeta(id, L))
        .filter(Boolean);
    const shown = metas.slice(0, maxIcons);
    const hidden = metas.length > maxIcons ? metas.slice(maxIcons) : [];

    useLayoutEffect(() => {
        if (!fadeOverflow || !metas.length) {
            setOverflowing(false);
            return undefined;
        }
        const el = stripRef.current;
        if (!el) return undefined;
        const check = () => {
            setOverflowing(el.scrollWidth > el.clientWidth + 0.5);
        };
        check();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(check);
        ro.observe(el);
        if (el.parentElement) ro.observe(el.parentElement);
        return () => ro.disconnect();
    }, [fadeOverflow, metas.length, shown.length, hidden.length]);

    if (!metas.length) return null;

    const stripClass = [
        'criteria-tag-strip',
        fadeOverflow ? 'criteria-tag-strip--clip' : '',
        fadeOverflow && overflowing ? 'criteria-tag-strip--fade' : '',
        className,
    ].filter(Boolean).join(' ');

    const strip = (
        <div ref={stripRef} className={stripClass} aria-hidden>
            {shown.map((x) => (
                <span
                    key={x.id}
                    className="criteria-tag-strip__icon-wrap"
                    title={showTooltip ? x.label : undefined}
                >
                    {x.icon ? (
                        <img src={x.icon} alt="" className="criteria-tag-strip__icon" />
                    ) : (
                        <span className="criteria-tag-strip__icon-fallback">{x.label[0] || '?'}</span>
                    )}
                </span>
            ))}
            {hidden.length > 0 && (
                <span
                    className="criteria-tag-strip__more"
                    title={showTooltip ? hidden.map((x) => x.label).join(', ') : undefined}
                >
                    ...
                </span>
            )}
        </div>
    );

    if (!showTooltip) return strip;

    return (
        <Tooltip
            content={(
                <div className="criteria-tag-strip-tooltip">
                    {metas.map((x) => (
                        <div key={x.id}>{x.label}</div>
                    ))}
                </div>
            )}
        >
            {strip}
        </Tooltip>
    );
};

export default CriteriaTagStrip;
